import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Wordmark } from "../components/PublicHeader";
import { RelocatingFromDialog } from "../components/RelocatingFromDialog";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const RELOCATING = "Relocating to Australia";
const SITUATIONS = ["Already in Australia", RELOCATING, "Moving cities in Australia", "Just looking for a new job"];
const inputCls = "mt-1.5 w-full rounded-lg border border-[#e3e0d3] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#94a01a]";
const labelCls = "text-xs uppercase tracking-[0.15em] font-medium text-[#51637a]";

export default function AuthCallback() {
  const hasProcessed = useRef(false);
  const [state, setState] = useState("processing");
  const [pending, setPending] = useState(null);
  const [role, setRole] = useState(localStorage.getItem("google_role_hint") || "candidate");
  const [form, setForm] = useState({ country_of_origin: "", situation: "", in_australia: null, company_name: "", job_title: "" });
  const [busy, setBusy] = useState(false);
  const [fromOpen, setFromOpen] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const isRelocating = form.situation === RELOCATING;

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = new URLSearchParams(window.location.hash.substring(1)).get("session_id");
    window.history.replaceState(null, "", window.location.pathname);
    if (!sessionId) return setState("error");
    api.post("/auth/google/session", { session_id: sessionId })
      .then(({ data }) => {
        if (!data.new_user) {
          setUser(data.user);
          navigate(data.user.role === "employer" ? "/employer" : "/candidate", { replace: true, state: { user: data.user } });
        } else {
          setPending(data);
          setState("complete_profile");
        }
      })
      .catch((e) => {
        toast.error(formatApiError(e));
        setState("error");
      });
  }, [navigate, setUser]);

  const complete = async (e) => {
    e.preventDefault();
    if (role === "candidate" && form.in_australia === null) return toast.error("Please tell us if you're currently living in Australia");
    if (role === "candidate" && !form.situation) return toast.error("Please tell us your current situation");
    if (role === "candidate" && isRelocating && !form.country_of_origin) {
      setFromOpen(true);
      return toast.error("Tell us where you're relocating from");
    }
    setBusy(true);
    try {
      const payload = { pending_token: pending.pending_token, role, ...form,
        country_of_origin: isRelocating ? form.country_of_origin : "Australia" };
      const { data } = await api.post("/auth/google/complete", payload);
      localStorage.removeItem("google_role_hint");
      setUser(data.user);
      toast.success("Welcome to The Ditch Careers!");
      const candidateDest = form.in_australia === false ? "/candidate" : "/candidate/onboarding";
      navigate(role === "employer" ? "/employer" : candidateDest, { replace: true, state: { user: data.user } });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (state === "processing")
    return (
      <div className="min-h-screen bg-[#f5f3ee] flex flex-col items-center justify-center gap-4" data-testid="google-auth-processing">
        <Loader2 size={34} className="animate-spin text-[#0d2f4d]" />
        <p className="text-sm text-[#51637a]">Signing you in with Google...</p>
      </div>
    );

  if (state === "error")
    return (
      <div className="min-h-screen bg-[#f5f3ee] flex flex-col items-center justify-center gap-4" data-testid="google-auth-error">
        <p className="text-[#0d2f4d] font-bold">Sign-in failed</p>
        <button onClick={() => navigate("/signin")} className="rounded-full bg-[#0d2f4d] text-white text-sm px-6 py-3">Back to sign in</button>
      </div>
    );

  return (
    <div className="min-h-screen bg-[#f5f3ee] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md bg-white rounded-lg border border-[#e3e0d3] p-8 sm:p-10" data-testid="google-complete-profile">
        <Wordmark />
        <h1 className="text-2xl font-bold text-[#0d2f4d] tracking-tight mt-6">Almost there, {pending?.name?.split(" ")[0] || "friend"}</h1>
        <p className="text-sm text-[#51637a] mt-1">Tell us a little more to finish setting up your account.</p>
        <form onSubmit={complete} className="mt-7 space-y-5">
          <div>
            <label className={labelCls}>I am...</label>
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              {["candidate", "employer"].map((r) => (
                <button key={r} type="button" onClick={() => setRole(r)} data-testid={`google-role-${r}`}
                  className={`rounded-full px-4 py-2.5 text-sm font-medium border transition-colors capitalize ${
                    role === r ? "bg-[#0d2f4d] text-white border-[#0d2f4d]" : "bg-white text-[#0d2f4d] border-[#e3e0d3] hover:border-[#0d2f4d]"}`}>
                  {r === "candidate" ? "A candidate" : "An employer"}
                </button>
              ))}
            </div>
          </div>
          {role === "candidate" ? (
            <>
              <div>
                <label className={labelCls}>Are you currently living in Australia?</label>
                <div className="mt-1.5 grid grid-cols-2 gap-2" data-testid="google-in-australia">
                  {[["Yes", true], ["No", false]].map(([label, val]) => (
                    <button key={label} type="button" onClick={() => setForm({ ...form, in_australia: val })}
                      data-testid={`google-in-australia-${label.toLowerCase()}`}
                      className={`rounded-full px-4 py-2.5 text-sm font-medium border transition-colors ${
                        form.in_australia === val ? "bg-[#0d2f4d] text-white border-[#0d2f4d]" : "bg-white text-[#0d2f4d] border-[#e3e0d3] hover:border-[#0d2f4d]"}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className={labelCls}>Your situation</label>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {SITUATIONS.map((s) => (
                    <button key={s} type="button"
                      onClick={() => {
                        setForm({ ...form, situation: s, country_of_origin: s === RELOCATING ? form.country_of_origin : "" });
                        if (s === RELOCATING) setFromOpen(true);
                      }}
                      data-testid={`google-situation-${s.toLowerCase().replace(/ /g, "-")}`}
                      className={`rounded-full px-4 py-2 text-xs font-medium border transition-colors ${
                        form.situation === s ? "bg-[#0d2f4d] text-white border-[#0d2f4d]" : "bg-white text-[#0d2f4d] border-[#e3e0d3] hover:border-[#0d2f4d]"}`}>
                      {s}
                    </button>
                  ))}
                </div>
                {isRelocating && form.country_of_origin && (
                  <p className="text-xs text-[#51637a] mt-2" data-testid="google-relocating-from-summary">
                    Relocating from <span className="font-medium text-[#0d2f4d]">{form.country_of_origin}</span>{" "}
                    <button type="button" onClick={() => setFromOpen(true)} className="text-[#7C8514] underline underline-offset-2">change</button>
                  </p>
                )}
              </div>
            </>
          ) : (
            <>
              <div>
                <label className={labelCls}>Company name</label>
                <input required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  data-testid="google-company" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Your job title</label>
                <input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })}
                  data-testid="google-jobtitle" className={inputCls} />
              </div>
            </>
          )}
          <button type="submit" disabled={busy} data-testid="google-complete-submit"
            className="w-full rounded-full bg-[#C1CC24] text-[#0d2f4d] font-semibold py-3.5 hover:bg-[#aeb81f] transition-colors disabled:opacity-60">
            {busy ? "Setting up..." : "Complete sign-up"}
          </button>
        </form>
      </div>
      <RelocatingFromDialog open={fromOpen} onOpenChange={setFromOpen}
        onSelect={(c) => setForm((f) => ({ ...f, country_of_origin: c }))} />
    </div>
  );
}
